/**
 * Consolidated Calculation Utilities
 *
 * Single source of truth for CapEx, ITC, and financial calculations.
 * Used by useBudget.js, budgetCalculations.js, and FinancialAnalysis.js.
 *
 * All functions are pure with no side effects or React dependencies.
 */

// ============================================================================
// CapEx Calculations
// ============================================================================

/**
 * Calculate equipment costs (PV, Battery, Generator)
 *
 * @param {Object} systemSizes - { pvKw, batteryKw, batteryKwh, generatorKw }
 * @param {Object} capexInputs - Budget inputs with rates and cost types
 * @returns {Object} Equipment cost breakdown
 */
export function calculateEquipmentCosts(systemSizes, capexInputs) {
    const { pvKw = 0, batteryKwh = 0, generatorKw = 0 } = systemSizes;

    // PV Turnkey cost (includes equipment + installation)
    const pvTurnkeyCost = capexInputs.pvTurnkey?.costType === 'lump_sum'
        ? (parseFloat(capexInputs.pvTurnkey?.totalCost) || 0)
        : pvKw * (parseFloat(capexInputs.pvTurnkey?.rate) || 0);

    // Battery equipment cost
    const batteryCost = capexInputs.battery?.costType === 'lump_sum'
        ? (parseFloat(capexInputs.battery?.totalCost) || 0)
        : batteryKwh * (parseFloat(capexInputs.battery?.rate) || 0);

    // Generator equipment cost
    const generatorCost = capexInputs.generator?.costType === 'lump_sum'
        ? (parseFloat(capexInputs.generator?.totalCost) || 0)
        : generatorKw * (parseFloat(capexInputs.generator?.rate) || 0);

    const total = pvTurnkeyCost + batteryCost + generatorCost;

    return {
        pvTurnkey: pvTurnkeyCost,
        battery: batteryCost,
        generator: generatorCost,
        total
    };
}

/**
 * Calculate installation costs (Battery and Generator only - PV is turnkey)
 *
 * @param {Object} systemSizes - { pvKw, batteryKw, batteryKwh, generatorKw }
 * @param {Object} capexInputs - Budget inputs with installation rates
 * @returns {Object} Installation cost breakdown
 */
export function calculateInstallationCosts(systemSizes, capexInputs) {
    const { batteryKwh = 0, generatorKw = 0 } = systemSizes;

    const batteryInstall = capexInputs.installation?.battery?.costType === 'lump_sum'
        ? (parseFloat(capexInputs.installation?.battery?.totalCost) || 0)
        : batteryKwh * (parseFloat(capexInputs.installation?.battery?.rate) || 0);

    const generatorInstall = capexInputs.installation?.generator?.costType === 'lump_sum'
        ? (parseFloat(capexInputs.installation?.generator?.totalCost) || 0)
        : generatorKw * (parseFloat(capexInputs.installation?.generator?.rate) || 0);

    const total = batteryInstall + generatorInstall;

    return {
        battery: batteryInstall,
        generator: generatorInstall,
        total
    };
}

/**
 * Calculate soft costs (percentage of equipment + installation base)
 *
 * @param {number} equipmentInstallationBase - Total of equipment + installation costs
 * @param {Object} softCostInputs - Soft cost rates/amounts
 * @returns {Object} Soft cost breakdown
 */
export function calculateSoftCosts(equipmentInstallationBase, softCostInputs) {
    const softCosts = softCostInputs || {};

    const calcCost = (item) => {
        if (!item) return 0;
        if (item.costType === 'lump_sum') {
            return parseFloat(item.totalCost) || 0;
        }
        return equipmentInstallationBase * (parseFloat(item.rate) || 0) / 100;
    };

    const engineering = calcCost(softCosts.engineering);
    const permits = calcCost(softCosts.permits);
    const projectManagement = calcCost(softCosts.projectManagement);
    const commissioning = calcCost(softCosts.commissioning);
    const contingency = calcCost(softCosts.contingency);
    const custom = calcCost(softCosts.custom);

    const total = engineering + permits + projectManagement + commissioning + contingency + custom;

    return {
        engineering,
        permits,
        projectManagement,
        commissioning,
        contingency,
        custom,
        total
    };
}

/**
 * Calculate total CapEx for given system sizes
 *
 * @param {Object} systemSizes - { pvKw, batteryKw, batteryKwh, generatorKw }
 * @param {Object} capexInputs - Budget rates from useBudget hook
 * @returns {Object} Complete CapEx breakdown
 */
export function calculateCapex(systemSizes, capexInputs) {
    const equipment = calculateEquipmentCosts(systemSizes, capexInputs);
    const installation = calculateInstallationCosts(systemSizes, capexInputs);
    const equipmentInstallationBase = equipment.total + installation.total;
    const softCosts = calculateSoftCosts(equipmentInstallationBase, capexInputs.softCosts);

    const total = equipmentInstallationBase + softCosts.total;

    return {
        equipment: equipment.total,
        installation: installation.total,
        softCostsTotal: softCosts.total,
        total,
        components: {
            pvTurnkey: { total: equipment.pvTurnkey },
            battery: { equipment: equipment.battery, installation: installation.battery },
            generator: { equipment: equipment.generator, installation: installation.generator }
        },
        softCosts: {
            engineering: softCosts.engineering,
            permits: softCosts.permits,
            projectManagement: softCosts.projectManagement,
            commissioning: softCosts.commissioning,
            contingency: softCosts.contingency,
            custom: softCosts.custom
        }
    };
}

// ============================================================================
// ITC (Investment Tax Credit) Calculations
// ============================================================================

/**
 * Calculate ITC for a single component
 *
 * @param {number} upfrontCost - Cost before ITC
 * @param {number} itcPercent - ITC percentage (0-100)
 * @param {boolean} includeItc - Whether ITC is enabled
 * @param {boolean} hasEligibleAssets - Whether project has ITC-eligible assets
 * @returns {Object} ITC calculation for component
 */
export function calculateComponentItc(upfrontCost, itcPercent, includeItc = true, hasEligibleAssets = true) {
    const effectivePercent = (includeItc && hasEligibleAssets) ? itcPercent : 0;
    const itcAmount = upfrontCost * (effectivePercent / 100);

    return {
        upfront: upfrontCost,
        itcPercent,
        itcAmount,
        netCost: upfrontCost - itcAmount
    };
}

/**
 * Calculate total ITC for all components
 *
 * @param {Object} capexBreakdown - Result from calculateCapex()
 * @param {Object} itcInputs - ITC percentages per component
 * @param {boolean} includeItc - Whether ITC is enabled
 * @param {Object} systemSizes - For determining ITC eligibility
 * @returns {Object} Complete ITC breakdown
 */
export function calculateItc(capexBreakdown, itcInputs, includeItc = true, systemSizes = {}) {
    const { pvKw = 0, batteryKwh = 0 } = systemSizes;

    // ITC eligibility: must have new solar OR new battery
    const hasItcEligibleAssets = pvKw > 0 || batteryKwh > 0;

    const calc = (upfront, percent) => calculateComponentItc(upfront, percent, includeItc, hasItcEligibleAssets);

    const pvTurnkey = calc(capexBreakdown.components?.pvTurnkey?.total || 0, itcInputs.pvTurnkey || 0);
    const batteryEquipment = calc(capexBreakdown.components?.battery?.equipment || 0, itcInputs.batteryEquipment || 0);
    const generatorEquipment = calc(capexBreakdown.components?.generator?.equipment || 0, itcInputs.generatorEquipment || 0);
    const batteryInstallation = calc(capexBreakdown.components?.battery?.installation || 0, itcInputs.batteryInstallation || 0);
    const generatorInstallation = calc(capexBreakdown.components?.generator?.installation || 0, itcInputs.generatorInstallation || 0);
    const engineering = calc(capexBreakdown.softCosts?.engineering || 0, itcInputs.engineering || 0);
    const permits = calc(capexBreakdown.softCosts?.permits || 0, itcInputs.permits || 0);
    const projectManagement = calc(capexBreakdown.softCosts?.projectManagement || 0, itcInputs.projectManagement || 0);
    const commissioning = calc(capexBreakdown.softCosts?.commissioning || 0, itcInputs.commissioning || 0);
    const contingency = calc(capexBreakdown.softCosts?.contingency || 0, itcInputs.contingency || 0);
    const custom = calc(capexBreakdown.softCosts?.custom || 0, itcInputs.custom || 0);

    const equipmentSubtotalItc = batteryEquipment.itcAmount + generatorEquipment.itcAmount;
    const installationSubtotalItc = batteryInstallation.itcAmount + generatorInstallation.itcAmount;
    const softCostsSubtotalItc = engineering.itcAmount + permits.itcAmount + projectManagement.itcAmount +
        commissioning.itcAmount + contingency.itcAmount + custom.itcAmount;
    const totalItc = pvTurnkey.itcAmount + equipmentSubtotalItc + installationSubtotalItc + softCostsSubtotalItc;

    const totalUpfront = capexBreakdown.total || 0;
    const netCostAfterItc = totalUpfront - totalItc;

    return {
        pvTurnkey,
        batteryEquipment,
        generatorEquipment,
        batteryInstallation,
        generatorInstallation,
        engineering,
        permits,
        projectManagement,
        commissioning,
        contingency,
        custom,
        equipmentSubtotalItc,
        installationSubtotalItc,
        softCostsSubtotalItc,
        totalItc,
        totalUpfront,
        netCostAfterItc,
        effectiveItcRate: totalUpfront > 0 ? (totalItc / totalUpfront * 100).toFixed(1) : 0,
        itcEnabled: includeItc,
        hasItcEligibleAssets
    };
}

// ============================================================================
// Financial Calculations (NPV, Payback, Cashflows)
// ============================================================================

/**
 * Calculate NPV of cash flows
 *
 * @param {Array} cashflows - Array of { year, netCashflow } objects
 * @param {number} discountRate - Discount rate as decimal (e.g., 0.08 for 8%)
 * @returns {number} Net present value
 */
export function calculateNpv(cashflows, discountRate = 0.08) {
    return cashflows.reduce((npv, cf) => {
        const discountFactor = Math.pow(1 + discountRate, cf.year);
        return npv + cf.netCashflow / discountFactor;
    }, 0);
}

/**
 * Calculate simple payback period
 *
 * @param {Array} cashflows - Array of { year, netCashflow } objects
 * @param {number} initialInvestment - Upfront cost (positive number)
 * @returns {number|null} Years to payback, or null if no payback in analysis period
 */
export function calculatePaybackPeriod(cashflows, initialInvestment) {
    let cumulativeCashflow = -initialInvestment;

    for (const cf of cashflows) {
        cumulativeCashflow += cf.netCashflow;
        if (cumulativeCashflow >= 0) {
            // Interpolate: how far into this year did cumulative cross zero?
            const cumulativeBeforeThisYear = cumulativeCashflow - cf.netCashflow;
            const fractionOfYear = Math.abs(cumulativeBeforeThisYear) / cf.netCashflow;
            return cf.year - 1 + fractionOfYear;
        }
    }

    return null; // No payback within analysis period
}

/**
 * Calculate total savings over analysis period
 *
 * @param {Array} cashflows - Array of { year, netCashflow, energySavings } objects
 * @param {number} initialInvestment - Upfront cost
 * @param {number} totalDerCharges - Total DER charges over period (optional)
 * @returns {Object} Savings summary
 */
export function calculateTotalSavings(cashflows, initialInvestment, totalDerCharges = 0) {
    const totalEnergySavings = cashflows.reduce((sum, cf) => sum + (cf.energySavings || 0), 0);
    const totalNetCashflow = cashflows.reduce((sum, cf) => sum + cf.netCashflow, 0);
    const netSavings = totalNetCashflow - initialInvestment;

    return {
        totalEnergySavings,
        totalNetCashflow,
        netSavings,
        totalDerCharges
    };
}

/**
 * Generate annual cash flows for analysis period
 *
 * @param {Object} params - Cash flow parameters
 * @param {number} params.analysisYears - Number of years to analyze
 * @param {number} params.yearOneSavings - First year energy savings
 * @param {number} params.yearOneOmCost - First year O&M cost
 * @param {number} params.yearOneFuelCost - First year fuel cost (legacy, used if delivery/commodity not provided)
 * @param {number} params.yearOneFuelDeliveryCost - First year fuel delivery cost (new)
 * @param {number} params.yearOneFuelCommodityCost - First year fuel commodity cost (new)
 * @param {number} params.yearOneDerCharges - First year DER charges
 * @param {Object} params.escalationRates - { energy, labor, naturalGas, naturalGasDelivery, naturalGasCommodity }
 * @param {number} params.degradationRate - PV degradation rate (optional)
 * @returns {Array} Annual cash flow objects
 */
export function calculateCashflows(params) {
    const {
        analysisYears = 20,
        yearOneSavings = 0,
        yearOneOmCost = 0,
        yearOneFuelCost = 0,
        yearOneFuelDeliveryCost = 0,
        yearOneFuelCommodityCost = 0,
        yearOneDerCharges = 0,
        escalationRates = { energy: 0.03, labor: 0.025, naturalGas: 0.025 },
        degradationRate = 0.005
    } = params;

    const cashflows = [];

    // Determine if we're using the new split delivery/commodity model
    // If both delivery and commodity are provided (even if zero), use them
    // Otherwise fall back to legacy yearOneFuelCost
    const useSplitFuelCosts = (yearOneFuelDeliveryCost !== 0 || yearOneFuelCommodityCost !== 0) ||
        (yearOneFuelDeliveryCost === 0 && yearOneFuelCommodityCost === 0 && yearOneFuelCost === 0);

    for (let year = 1; year <= analysisYears; year++) {
        // Energy savings escalate at energy rate, with PV degradation
        const degradationFactor = Math.pow(1 - degradationRate, year - 1);
        const escalatedSavings = yearOneSavings *
            Math.pow(1 + escalationRates.energy, year - 1) *
            degradationFactor;

        // O&M escalates at labor rate
        const escalatedOmCost = yearOneOmCost *
            Math.pow(1 + escalationRates.labor, year - 1);

        // Fuel cost calculation with separate delivery/commodity escalation
        let escalatedFuelCost, escalatedDelivery, escalatedCommodity;

        if (useSplitFuelCosts) {
            // Escalate delivery and commodity separately
            // Use specific rates if available, fall back to legacy naturalGas rate
            const deliveryRate = escalationRates.naturalGasDelivery ?? escalationRates.naturalGas;
            const commodityRate = escalationRates.naturalGasCommodity ?? escalationRates.naturalGas;

            escalatedDelivery = yearOneFuelDeliveryCost *
                Math.pow(1 + deliveryRate, year - 1);
            escalatedCommodity = yearOneFuelCommodityCost *
                Math.pow(1 + commodityRate, year - 1);
            escalatedFuelCost = escalatedDelivery + escalatedCommodity;
        } else {
            // Legacy: escalate total fuel cost at naturalGas rate
            escalatedFuelCost = yearOneFuelCost *
                Math.pow(1 + escalationRates.naturalGas, year - 1);
            escalatedDelivery = 0;
            escalatedCommodity = 0;
        }

        // DER charges escalate at energy rate
        const escalatedDerCharges = yearOneDerCharges *
            Math.pow(1 + escalationRates.energy, year - 1);

        const netCashflow = escalatedSavings - escalatedOmCost - escalatedFuelCost - escalatedDerCharges;

        cashflows.push({
            year,
            energySavings: escalatedSavings,
            omCost: escalatedOmCost,
            fuelCost: escalatedFuelCost,
            fuelDeliveryCost: escalatedDelivery,
            fuelCommodityCost: escalatedCommodity,
            derCharges: escalatedDerCharges,
            netCashflow
        });
    }

    return cashflows;
}

// ============================================================================
// OpEx Calculations
// ============================================================================

/**
 * Calculate annual operating costs
 *
 * @param {Object} systemSizes - { pvKw, batteryKw, batteryKwh, generatorKw }
 * @param {Object} opexInputs - OpEx rates and settings
 * @returns {Object} Annual OpEx breakdown
 */
export function calculateOpex(systemSizes, opexInputs) {
    const { pvKw = 0, batteryKwh = 0, generatorKw = 0 } = systemSizes;

    const pvOM = opexInputs.pv?.costType === 'per_kw_year'
        ? pvKw * (parseFloat(opexInputs.pv?.rate) || 0)
        : (parseFloat(opexInputs.pv?.rate) || 0);

    const batteryOM = opexInputs.battery?.costType === 'per_kwh_year'
        ? batteryKwh * (parseFloat(opexInputs.battery?.rate) || 0)
        : (parseFloat(opexInputs.battery?.rate) || 0);

    const generatorOM = opexInputs.generator?.costType === 'per_kw_year'
        ? generatorKw * (parseFloat(opexInputs.generator?.rate) || 0)
        : (parseFloat(opexInputs.generator?.rate) || 0);

    const fixedAnnualOM = parseFloat(opexInputs.fixedAnnual?.rate) || 0;
    const customOM = parseFloat(opexInputs.custom?.rate) || 0;

    const totalAnnualOM = pvOM + batteryOM + generatorOM + fixedAnnualOM + customOM;

    return {
        pvOM,
        batteryOM,
        generatorOM,
        fixedAnnualOM,
        customOM,
        totalAnnualOM,
        fuel: opexInputs.fuel
    };
}

// ============================================================================
// Convenience: Combined CapEx with ITC
// ============================================================================

/**
 * Calculate CapEx with ITC applied (convenience wrapper)
 *
 * @param {Object} systemSizes - System sizes
 * @param {Object} capexInputs - CapEx inputs
 * @param {Object} itcSettings - { includeItc: boolean, inputs: {...} }
 * @returns {Object} CapEx with ITC summary
 */
export function calculateCapexWithItc(systemSizes, capexInputs, itcSettings = null) {
    const capex = calculateCapex(systemSizes, capexInputs);

    let totalItc = 0;
    let capexAfterIncentives = capex.total;
    let itcApplied = false;

    if (itcSettings?.includeItc && itcSettings?.inputs) {
        const itc = calculateItc(capex, itcSettings.inputs, true, systemSizes);
        totalItc = itc.totalItc;
        capexAfterIncentives = itc.netCostAfterItc;
        itcApplied = true;
    }

    return {
        totalCapex: capex.total,
        capexAfterIncentives,
        totalItc,
        itcApplied,
        breakdown: {
            equipment: {
                pvTurnkey: capex.components.pvTurnkey.total,
                battery: capex.components.battery.equipment,
                generator: capex.components.generator.equipment,
                total: capex.equipment
            },
            installation: {
                battery: capex.components.battery.installation,
                generator: capex.components.generator.installation,
                total: capex.installation
            },
            softCosts: {
                total: capex.softCostsTotal,
                base: capex.equipment + capex.installation
            }
        }
    };
}
